import { pmc_menu, subs_menus, vendor_menus } from "@/lib/menu";
import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { organization_type } from "@/models/organization.model.ts";
import { menuService } from "@/lib/permissions/menu.service";
import { api } from "@/lib/api";
import { CONTACT_PROPERTIES_MENU_ITEM, CONTACT_SCOPE, contactMenu, isPropertyContact } from "@/lib/property-contact-scope";
import type { ContactProperty } from "@shyne/shared/api";
import type { uuid } from "@/interfaces";
import * as Sentry from "@sentry/react";
import { sentryEnabled } from "@/instrument";

export type RouteScope = organization_type | typeof CONTACT_SCOPE;

interface InitialStateType {
  routeScope: RouteScope | null;
  contactProperties: ContactProperty[];
  scopeRequest: string | null;
  scopeWithoutOrganizationFor: uuid | null;
  accountUnavailable: boolean;
  menu: {
    title: string;
    url: string;
    icon: string;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
    }[];
  }[];
  loading: boolean;
  error: string | null;
  calendarSelectedDate: string; // ISO string for persistence
}

const initialState: InitialStateType = {
  routeScope: null,
  contactProperties: [],
  scopeRequest: null,
  scopeWithoutOrganizationFor: null,
  accountUnavailable: false,
  menu: [],
  loading: true,
  error: null,
  calendarSelectedDate: new Date().toISOString(),
};

async function contactPropertiesOrNone(): Promise<ContactProperty[]> {
  try {
    return (await api.me()).contactProperties;
  } catch (error) {
    if (sentryEnabled) Sentry.captureException(error);
    return [];
  }
}

export const loadPermissionAwareMenu = createAsyncThunk(
  "config/loadPermissionAwareMenu",
  async (organizationType: organization_type) => {
    const [filteredMenu, contactProperties] = await Promise.all([menuService.getFilteredMenu(organizationType), contactPropertiesOrNone()]);
    const menu = isPropertyContact(contactProperties) ? [...filteredMenu, CONTACT_PROPERTIES_MENU_ITEM] : filteredMenu;
    return { organizationType, menu, contactProperties };
  }
);

export const ACCOUNT_UNAVAILABLE = "account-unavailable";

type ConfigState = { config: InitialStateType };

export const loadScopeWithoutOrganization = createAsyncThunk(
  "config/loadScopeWithoutOrganization",
  async (_userId: uuid, { rejectWithValue }) => {
    let contactProperties: ContactProperty[];
    try {
      contactProperties = (await api.me()).contactProperties;
    } catch (error) {
      if (sentryEnabled) Sentry.captureException(error);
      return rejectWithValue(ACCOUNT_UNAVAILABLE);
    }
    if (isPropertyContact(contactProperties)) return { routeScope: CONTACT_SCOPE, menu: contactMenu(contactProperties), contactProperties };
    return { routeScope: organization_type.emp, menu: await menuService.getFilteredMenu(organization_type.emp), contactProperties };
  },
  { condition: (userId, { getState }) => (getState() as ConfigState).config.scopeWithoutOrganizationFor !== userId },
);

const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    clearMenuError: (state) => {
      state.error = null;
    },
    setCalendarSelectedDate: (state, action: PayloadAction<string>) => {
      state.calendarSelectedDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadPermissionAwareMenu.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.scopeRequest = action.meta.requestId;
        state.scopeWithoutOrganizationFor = null;
        state.accountUnavailable = false;
      })
      .addCase(loadPermissionAwareMenu.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.scopeRequest) return;
        state.loading = false;
        state.routeScope = action.payload.organizationType;
        state.contactProperties = action.payload.contactProperties;
        state.menu = action.payload.menu;
      })
      .addCase(loadScopeWithoutOrganization.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.scopeRequest = action.meta.requestId;
        state.scopeWithoutOrganizationFor = action.meta.arg;
        state.accountUnavailable = false;
      })
      .addCase(loadScopeWithoutOrganization.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.scopeRequest) return;
        state.loading = false;
        state.routeScope = action.payload.routeScope;
        state.contactProperties = action.payload.contactProperties;
        state.menu = action.payload.menu;
      })
      .addCase(loadScopeWithoutOrganization.rejected, (state, action) => {
        if (action.meta.requestId !== state.scopeRequest) return;
        state.loading = false;
        state.error = action.error.message || "Failed to load menu";
        state.menu = [];
        state.scopeWithoutOrganizationFor = null;
        state.accountUnavailable = action.payload === ACCOUNT_UNAVAILABLE;
      })
      .addCase(loadPermissionAwareMenu.rejected, (state, action) => {
        if (action.meta.requestId !== state.scopeRequest) return;
        state.loading = false;
        state.error = action.error.message || "Failed to load menu";
        // Fallback to static menu on error
        switch (state.routeScope) {
          case organization_type.pmc:
            state.menu = pmc_menu;
            break;
          case organization_type.vendor:
            state.menu = vendor_menus;
            break;
          case organization_type.subs:
            state.menu = subs_menus;
            break;
          default:
            state.menu = [];
            break;
        }
      });
  },
});

export const { clearMenuError, setCalendarSelectedDate } = configSlice.actions;
export default configSlice.reducer;
